/**
 * usePlacementVersicherer — TanStack Query hooks for the Ausschreibungs-Screen Tab 3a (Versicherer-
 * Ranking & Auswahl, Prozessschritt 5; P2 Placement Slice 7).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*; mock mode resolves the
 * Ü3 fixture (AXA 14 / Zurich 9 / Helvetia 21 / Allianz 3 / Versicherer E 0 aktive Policen) so the
 * tab renders standalone. The server owns every decision (stats recompute, gating, selection diff) —
 * the UI only triggers + renders. The two mutations invalidate the ranking + auswahl keys + the
 * placement detail (the header chips live on every tab).
 *
 * [Source: apps/web/src/features/placements/usePlacementSnapshot.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  VERSICHERER_AUSWAHL_STATUS,
  VERSICHERER_EIGNUNG,
  chipsFromAuswahl,
  sortVersichererRanking,
} from '@brokly/shared';
import type {
  VersichererAuswahlEintrag,
  VersichererAuswahlPutBody,
  VersichererAuswahlResponse,
  VersichererKennzahl,
  VersichererRankingEintrag,
  VersichererRankingResponse,
} from '@brokly/shared';

// ── Ü3 mock fixture (mock mode only) ─────────────────────────────────────────────

const AUSGEWAEHLT = ['CHE-AXA', 'CHE-ZUR', 'CHE-HEL'];

function kennzahl(total: number, aktiv: number, letzte: string | null): VersichererKennzahl {
  return { anzahl_policen_total: total, anzahl_policen_aktiv: aktiv, letzte_platzierung_am: letzte };
}

function mockEintrag(uid: string, name: string, aktiv: number, total: number, letzte: string | null, email: string | null): VersichererRankingEintrag {
  const kz = kennzahl(total, aktiv, letzte);
  return {
    rang: 0,
    carrier_uid: uid,
    name,
    logo_url: null,
    email_submission: email,
    submission_faehig: email !== null,
    eignung: VERSICHERER_EIGNUNG.GEEIGNET,
    eignung_gruende: [`${total} Policen dieser Sparte im Bestand (${aktiv} aktiv)`],
    sparte: kz,
    familie: kz,
    gesamt: kz,
    ausgewaehlt: AUSGEWAEHLT.includes(uid),
    auswahl_status: AUSGEWAEHLT.includes(uid) ? VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT : null,
    auswahl_gesperrt: false,
  };
}

function mockRanking(id: string): VersichererRankingResponse {
  const raw = [
    mockEintrag('CHE-AXA', 'AXA', 14, 18, '2026-03-01', 'ausschreibung@axa.ch'),
    mockEintrag('CHE-ZUR', 'Zurich', 9, 12, '2026-02-10', 'offerten@zurich.ch'),
    mockEintrag('CHE-HEL', 'Helvetia', 21, 25, '2026-04-05', 'broker@helvetia.ch'),
    mockEintrag('CHE-ALL', 'Allianz', 3, 4, '2025-11-20', null),
    mockEintrag('CHE-E', 'Versicherer E', 0, 2, '2024-09-01', 'info@versicherer-e.ch'),
  ];
  const sorted = sortVersichererRanking(raw);
  sorted.forEach((e, i) => (e.rang = i + 1));
  return {
    placement_id: id,
    status: 'vorgaben_erfasst',
    branchencode: '21000',
    branchen_label: 'Betriebs- und Berufshaftpflicht Versicherung',
    familie: '20000',
    familie_label: 'Haftpflichtversicherungen',
    auswahl_erlaubt: true,
    auswahl_sperrgrund: null,
    stats: { computed_at: new Date().toISOString(), stale: false, policies_counted: 61, carriers_counted: 5, ausloeser: 'erststart', refreshed: true, skip_grund: null, naechste_aktualisierung_ab: null },
    eignung_quelle: 'bestand',
    versicherer: sorted,
  };
}

function mockAuswahl(id: string): VersichererAuswahlResponse {
  const ranking = mockRanking(id);
  const items: VersichererAuswahlEintrag[] = ranking.versicherer
    .filter((v) => AUSGEWAEHLT.includes(v.carrier_uid))
    .map((v) => ({
      id: `sel-${v.carrier_uid}`,
      carrier_uid: v.carrier_uid,
      name: v.name,
      logo_url: v.logo_url,
      status: VERSICHERER_AUSWAHL_STATUS.AUSGEWAEHLT,
      rang_bei_auswahl: v.rang,
      eignung_bei_auswahl: v.eignung,
      aktive_policen_bei_auswahl: v.familie.anzahl_policen_aktiv,
      selected_at: new Date().toISOString(),
      selected_by: null,
      selected_by_name: null,
      removed_at: null,
      submission_id: null,
    }));
  return {
    placement_id: id,
    status: 'vorgaben_erfasst',
    auswahl_erlaubt: true,
    auswahl_sperrgrund: null,
    items,
    chips: chipsFromAuswahl(items),
    changed: false,
    added: [],
    removed: [],
  };
}

// ── Queries ──────────────────────────────────────────────────────────────────────

export function usePlacementVersichererRanking(id: string) {
  const live = isLiveData();
  return useQuery<VersichererRankingResponse>({
    queryKey: qk.placementVersichererRanking(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getVersichererRanking(id) : Promise.resolve(mockRanking(id))),
    ...(live ? {} : { initialData: () => mockRanking(id), staleTime: Infinity }),
  });
}

export function usePlacementVersichererAuswahl(id: string) {
  const live = isLiveData();
  return useQuery<VersichererAuswahlResponse>({
    queryKey: qk.placementVersichererAuswahl(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getVersichererAuswahl(id) : Promise.resolve(mockAuswahl(id))),
    ...(live ? {} : { initialData: () => mockAuswahl(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function useVersichererInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'versicherer-ranking', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'versicherer-auswahl', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'detail', id] });
  };
}

export function useRefreshVersichererStats(id: string) {
  const live = isLiveData();
  const invalidate = useVersichererInvalidate(id);
  return useMutation({
    mutationFn: () => (live ? apiClient.placements.refreshVersichererStats(id) : Promise.resolve(mockRanking(id))),
    onSuccess: invalidate,
  });
}

export function usePutVersichererAuswahl(id: string) {
  const live = isLiveData();
  const invalidate = useVersichererInvalidate(id);
  return useMutation({
    mutationFn: (body: VersichererAuswahlPutBody) =>
      live ? apiClient.placements.putVersichererAuswahl(id, body) : Promise.resolve(mockAuswahl(id)),
    onSuccess: invalidate,
  });
}
